<template lang="pug">
q-dialog.events-archive-modal(v-model="show", @hide="closeModal")
  .events-archive-modal-card.schedules-new-form
    .events-archive-modal-header
      .events-archive-modal-title {{ title }}
      q-icon.events-archive-modal-close(name="close", role="button", tabindex="0", @click="show = false")

    .spinner-container.events-archive-modal-spinner(v-if="loading")
      q-spinner(color="primary", size="3em")

    .events-archive-modal-empty(v-else-if="events.length === 0") {{ schedulerLocales.noArchivedEvents }}

    .events-archive-modal-events(v-else)
      event-el(
        v-for="(event, index) in events",
        :key="event.id",
        :index="index",
        :event="event",
        :readonly="true",
        :archiveMode="true",
        @restore-event="requestRestore"
      )

    q-dialog(v-model="confirmModalShown")
      confirm-modal(@confirm="handleRestoreConfirmation", :parentData="{ message: restoreConfirmMessage }")
</template>

<script setup>
import { computed, onBeforeMount, provide, ref } from "vue";
import { format, parseISO } from "date-fns";
import { Notify } from "quasar";
import backend from "@/api";
import i18n from "@/plugins/vue-i18n";
import { handleError } from "@/services/handleErrors";
import { currentLocale, pprScheduleLocales } from "@/services/useLocales";
import confirmModal from "../../../shared/helpers/confirmModal.vue";
import eventEl from "./form/event.vue";

const props = defineProps({
  show: { type: Boolean, default: false },
  scheduleId: { type: [Number, String], required: true },
});

const emit = defineEmits(["close"]);

const show = ref(props.show);
const title = ref("");
const events = ref([]);
const loading = ref(true);
const confirmModalShown = ref(false);
const selectedEventId = ref(null);
const restoring = ref(false);

const schedulerLocales = computed(() => i18n["messages"][currentLocale.value]["scheduler"]);
const dateLocales = computed(() => i18n["messages"][currentLocale.value]["date"]);
const restoreConfirmMessage = computed(() => schedulerLocales.value.restoreArchivedEventConfirm);
const periodTypeOptions = computed(() => [
  { id: "daily", title: pprScheduleLocales.value["form.daily"] },
  { id: "monthly_date", title: pprScheduleLocales.value["form.monthly_date"] },
  { id: "weekly", title: pprScheduleLocales.value["form.weekly"] },
  { id: "monthly", title: pprScheduleLocales.value["form.monthly"] },
  { id: "yearly", title: pprScheduleLocales.value["form.yearly"] },
]);

const closeModal = () => {
  show.value = false;
  emit("close");
};

const formatDate = value => {
  if (!value) {
    return "";
  }

  const parsedDate = parseISO(value);
  return Number.isNaN(parsedDate.getTime()) ? value : format(parsedDate, "dd.MM.yyyy");
};

const getPeriodOptions = periodType => {
  switch (periodType) {
    case "daily":
      return [
        { id: "Monday", title: dateLocales.value.daysShort[1], selected: false },
        { id: "Tuesday", title: dateLocales.value.daysShort[2], selected: false },
        { id: "Wednesday", title: dateLocales.value.daysShort[3], selected: false },
        { id: "Thursday", title: dateLocales.value.daysShort[4], selected: false },
        { id: "Friday", title: dateLocales.value.daysShort[5], selected: false },
        { id: "Saturday", title: dateLocales.value.daysShort[6], selected: false },
        { id: "Sunday", title: dateLocales.value.daysShort[0], selected: false },
      ];
    case "monthly_date":
      return Array.from({ length: 31 }, (_, index) => ({ id: index + 1, title: index + 1, selected: false }));
    case "weekly":
      return Array.from({ length: 52 }, (_, index) => ({ id: index + 1, title: index + 1, selected: false }));
    case "monthly":
      return Array.from({ length: 12 }, (_, index) => {
        const date = new Date(2022, index, 1);
        return {
          id: date.toLocaleString("en", { month: "long" }),
          title: date.toLocaleString(currentLocale.value, { month: "long" }),
          selected: false,
        };
      });
    case "yearly": {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 16 }, (_, index) => ({
        id: currentYear + index + 1,
        title: currentYear + index + 1,
        selected: false,
      }));
    }
    default:
      return [];
  }
};

const mapWorks = eventWorks => ({
  options: [],
  items: (eventWorks || []).map(eventWork => {
    const work = eventWork.work || eventWork;
    return {
      id: work.id ?? eventWork.work_id ?? eventWork.id,
      title: work.title || "",
      cost: work.cost,
      standard_hours: work.standard_hours,
      count: Math.max(Number(eventWork.count || 1) - 1, 0),
      selected: true,
    };
  }),
  toRemove: [],
  hasNextPage: false,
  page: 1,
});

const mapArchivedEvent = item => {
  const period = getPeriodOptions(item.period_type);
  (item.period || []).forEach(periodId => {
    const periodOption = period.find(option => option.id.toString() === periodId.toString());
    if (periodOption) {
      periodOption.selected = true;
    }
  });

  return {
    id: item.id,
    actions: item.actions || [],
    name: item.title,
    description: item.description || "",
    periodType: periodTypeOptions.value.find(option => option.id === item.period_type) || {
      id: item.period_type,
      title: item.period_type,
    },
    period,
    startedAt: formatDate(item.started_at),
    finishedAt: formatDate(item.finished_at),
    deadline: item.days_to_overdue ?? "",
    checklistId: item.checklist_id ?? item.checklist?.id ?? item.checklist?.value ?? null,
    checklistRequired: Boolean(item.checklist_required),
    works: mapWorks(item.event_works),
    materials: item.event_materials || [],
    materialsToRemove: [],
    materialsOptions: [],
    archived: item.archived,
  };
};

const loadArchivedEvents = async () => {
  loading.value = true;
  try {
    const { data } = await backend.index(`/api/v3/ppr/schedules/${props.scheduleId}/archived_events`);
    title.value = data.title || "";
    events.value = (data.schedule_events || []).map(mapArchivedEvent);
  } catch (error) {
    await handleError(error, { notify: true });
  } finally {
    loading.value = false;
  }
};

const requestRestore = eventId => {
  if (restoring.value) {
    return;
  }

  selectedEventId.value = eventId;
  confirmModalShown.value = true;
};

const handleRestoreConfirmation = async confirmed => {
  confirmModalShown.value = false;
  if (!confirmed || selectedEventId.value === null || restoring.value) {
    selectedEventId.value = null;
    return;
  }

  const eventId = selectedEventId.value;
  restoring.value = true;
  try {
    await backend.create(`/api/v3/ppr/schedules/${props.scheduleId}/restore_events`, "", {
      event_id: eventId,
    });
    events.value = events.value.filter(event => event.id !== eventId);
    Notify.create(schedulerLocales.value.restoreArchivedEventSuccess.replace(":id", eventId));
  } catch (error) {
    await handleError(error, { notify: true });
  } finally {
    selectedEventId.value = null;
    restoring.value = false;
  }
};

provide("formVariables", { periodTypeOptions });

onBeforeMount(loadArchivedEvents);
</script>

<style scoped lang="scss">
.events-archive-modal-card.schedules-new-form {
  position: relative;
  top: auto;
  right: auto;
  width: min(1400px, calc(100vw - 80px));
  min-height: 300px;
  height: min(800px, calc(100vh - 80px));
  padding: 30px;
  border-radius: 30px;
  overflow-y: auto;
}

.events-archive-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
  margin-bottom: 25px;
  border-bottom: 1px solid var(--field-border);
}

.events-archive-modal-title {
  padding-right: 30px;
  color: var(--dashboard-label-color);
  font-size: 28px;
  font-weight: 500;
}

.events-archive-modal-close {
  color: #78839c;
  cursor: pointer;
  font-size: 24px;
}

.events-archive-modal-spinner {
  min-height: 200px;
}

.events-archive-modal-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% - 100px);
  color: var(--dashboard-label-color);
  font-size: 16px;
  text-align: center;
}

.events-archive-modal-events {
  width: 100%;
}

.events-archive-modal-card :deep(.form-step-four-event-works-form.readonly) {
  display: block;
}

.events-archive-modal-card :deep(.form-step-four-event-works-options.selected.readonly) {
  width: 100%;
}

.events-archive-modal-card :deep(.form-step-four-event-works-option-name) {
  cursor: default;
}
</style>
